import React from "react";
import { Link, useSearchParams } from "react-router-dom";
import {
  FaExclamationTriangle,
  FaSync,
  FaEnvelope,
  FaHome,
} from "react-icons/fa";
import styled from "styled-components";

/* -------------------------------------------------------
 * CONSTANTS
 * ----------------------------------------------------- */
const PRIMARY_COLOR = "#182848";

/* -------------------------------------------------------
 * STYLED COMPONENTS
 * ----------------------------------------------------- */
const PageWrapper = styled.div`
  min-height: 100vh;
  display: flex;
  align-items: center;
  justify-content: center;
  background: linear-gradient(135deg, #fffdf5, #fff8e1);
  padding: 1rem;
`;

const Card = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 12px 36px rgba(0, 0, 0, 0.14);
  padding: 2.5rem;
  max-width: 600px;
  width: 100%;
  text-align: center;
  border: 1px solid #e0e4ea;
`;

const Icon = styled(FaExclamationTriangle)`
  font-size: 4rem;
  color: #ffc107;
  margin-bottom: 1rem;
`;

const Title = styled.h1`
  font-size: 2.2rem;
  font-weight: 700;
  margin-bottom: 0.5rem;
`;

const Subtitle = styled.p`
  font-size: 1.05rem;
  color: #555;
  margin-bottom: 1.5rem;
`;

const InfoBox = styled.div`
  background: #fff3cd;
  border-radius: 10px;
  padding: 1rem;
  margin: 1.5rem 0;
  font-size: 0.95rem;
  color: #856404;
  text-align: left;
  border: 1px solid #ffeeba;
`;

const ActionGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;

  @media (min-width: 768px) {
    flex-direction: row;
    justify-content: center;
  }
`;

const Button = styled.button`
  background-color: ${PRIMARY_COLOR};
  color: white;
  border: none;
  border-radius: 10px;
  padding: 0.75rem 1.5rem;
  font-weight: 600;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 0.5rem;
  cursor: pointer;
  transition: all 0.2s ease;

  &:hover {
    background-color: #132038;
    transform: translateY(-2px);
  }

  &.outline {
    background: transparent;
    color: ${PRIMARY_COLOR};
    border: 2px solid ${PRIMARY_COLOR};

    &:hover {
      background-color: rgba(24, 40, 72, 0.1);
    }
  }
`;

const ContactLink = styled.a`
  display: inline-flex;
  align-items: center;
  gap: 0.4rem;
  margin-top: 1.25rem;
  color: ${PRIMARY_COLOR};
  font-weight: 500;
  text-decoration: none;

  &:hover {
    text-decoration: underline;
  }
`;

/* -------------------------------------------------------
 * COMPONENT
 * ----------------------------------------------------- */
const PaymentPendingPage = () => {
  const [searchParams] = useSearchParams();
  const orderId = searchParams.get("order_id");

  return (
    <PageWrapper>
      <Card>
        <Icon aria-hidden="true" />
        <Title>Payment Pending</Title>

        <Subtitle>
          We’re still confirming your payment. This usually takes a few moments.
        </Subtitle>

        <InfoBox>
          <strong>Please note:</strong>
          <ul style={{ marginTop: "0.5rem" }}>
            <li>Do <strong>not</strong> retry the payment if money was deducted.</li>
            <li>Your bank may take a short time to confirm the transaction.</li>
            <li>If payment succeeds, your registration will be updated automatically.</li>
          </ul>
        </InfoBox>

        <ActionGroup>
          <Link to="/" style={{ textDecoration: "none" }}>
            <Button>
              <FaHome /> Go Home
            </Button>
          </Link>

          <Link
            to={`/payment-response${orderId ? `?order_id=${orderId}` : ""}`}
            style={{ textDecoration: "none" }}
          >
            <Button className="outline">
              <FaSync /> Check Status Again
            </Button>
          </Link>
        </ActionGroup>

        <ContactLink
          href={`mailto:info@q2d.in?subject=Payment Pending - Order ${orderId || ""}`}
        >
          <FaEnvelope /> Contact Support
        </ContactLink>
      </Card>
    </PageWrapper>
  );
};

export default PaymentPendingPage;
